import React from 'react';
import './deep-dives-styles.css';
import Header from '../components/header';
import GoToTop from '../components/go-to-top';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Articol from '../components/article';
import layers from '../assets/layers.png';
import layers2 from '../assets/layers2.png';
import testusecase from '../assets/testusecase.png';
import new_admision_use_case from '../assets/new_admission_use_case.png';
import dependency_inversion from '../assets/dependency-inversion.png';
import get_string from '../assets/get_string.png';

class GoLangWebAppArchitecture extends React.Component {
    sendEvent = () => {
        if(window.location.hostname === 'localhost' || window.location.hostname.indexOf('192.168.0') === 0) {
            return;
        }

        if(!sessionStorage.getItem('post-architecture')) {
            sessionStorage.setItem('post-architecture', true);

            fetch('https://api.ipify.org?format=json')
                .then((ipResp) => ipResp.json().then((ipJson) => {
                    fetch('https://09mclyfv7k.execute-api.eu-central-1.amazonaws.com/prod/log_event', {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({type: 'post-architecture', name: 'binaryfusion.ro', from: ipJson}),
                    }).then((response) => response.json().then((responseData) => {
                        sessionStorage.setItem('success', responseData.EventId);
                    }));
                })
            );
        }
    }
    componentDidMount() {
        if(window.location.hostname !== 'localhost' && window.location.hostname.indexOf('192.168.0') === -1) {
            window.scrollTo(0,0);
        }

        this.sendEvent();
    }

    render() {
        const codeStyle = {
            'textShadow': 'none',
            'border': 'none',
            'boxShadow': 'none',
        };

        return (
            <div>
                <Header />
                <GoToTop />

                <div className='page'>
                    <h1 className='title'>GoLang Webapp on AWS (Part 4) - Architecture</h1>
                    <div className='paragraph'>
                        Updated on 02 Oct 2024
                    </div>

                    <div className='paragraph'>
                        In this article I want to show how I implemented a Clean Layered Architecture using Clean Code Principles for the server side code, that is for the Go Lambda function. I call it <strong>Clean Layered Architecture</strong>.
                    </div>

                    <h5 className='pl-0'>The theory</h5>

                    <div className='paragraph'>
                        <h5 className='title'>Why do I need architecture?</h5>
                        I built this project to learn about what services AWS is offering - a web application with React on front-end, hosted on Amplify, an Api Gateway, a Lambda function
                        running Go lang from a Docker container and a DynamoDB for persistent storage. I considered in a serious manner the need to have an architecture based on two factors: <br/>
                        - I am planning to create a real world live application starting from this template<br/>
                        - I already created a cross-platform mobile app that uses the same general layout of architecture and I want to share it with you and get some feedback
                    </div>

                    <div className='paragraph'>
                        The fact that I plan to grow this template in a more mature project translates, in my mind into few characteristics. The most important attributes I need to have are these: <br/>
                        - scalable<br/>
                        - maintainable<br/>
                        - flexible<br/><br/>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Scalable</h5>
                        I want to point out that this is not a course or a tutorial. Scalable refers to the fact that I want to grow this project from a case study that it is now to
                        a full fledged widely used web app. Also refers to the fact that I don't yet know, and I don't want to assume that I could know all the features and the requirements
                        my project will have in the future. All this will adapt and change and pivot based on the customers, the business model or other factors that I am not aware of, presently. 
                        How do you defend against things you don't know? Having a well implemented architecture will help a great deal in this aspect.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Maintainable</h5>
                        This refers to fixing bugs and more. Every time a problem arises either from simply a bug but also from a feature not well optimized or a change that a user is requesting
                        I want to be able to fix it in the most efficient way possible, with the least amount of lines affected and with ideally zero side-effects other then what is requested.
                        I want to quickly find what needs to change, where the change should be added and what exactly is that change. This allows me to quickly react to my users needs. 
                        This is a property of a code following the principles of 
                        <strong> Clean Code</strong>. Using an architecture will force you to apply these principles.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Flexible</h5>
                        Flexibility here refers to the fact that I want the project to be somewhat protected from framework and library updates. In my experience, since I could not work or didn't want to work for continuous
                        periods of time on a project, the next time I resume the work, it's very hard or straight impossible to make all the updates and continue working more or less without
                        problems. What I needed was a way to make the updates, or in extreme cases replace the libraries without rewriting the whole thing. This is exactly what a 
                        correctly chosen architecture is providing.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Clean architecture</h5>
                        The intent behind Clean Architecture is to protect high-level business code from lowlevel implementation details. The business code refers to that part of the source code that replicates real world concepts. In theory 
                        you should be able to reuse this part of the code in any other application related to the same business domain without much change. Low level code refers to the code you wrote to implement your idea of functionality. That is probably much more likely to change than real world business rules.<br/><br/>
                        There are many very good books out there for explaining the concepts and every type of architecture in great detail such as the famous <strong>Clean Architecture</strong> by Robert C. Martin. I found very helpful information in the book <strong>Clean Mobile Architecture</strong> by Petros Efthymiou.
                        To say that you follow a Clean Architecture is to adhere to these principles: <br/>
                        - Code separation in layers of different levels<br/>
                        - The Dependency Rule<br/>
                        - Dependency Inversion<br/><br/>
                        <strong>Code separation in layers of different levels</strong> - This is where you get to decide how many groups of classes you will have based on how probable are they likely to change.<br/>
                        <strong>The Dependency Rule</strong> - Your code should never depend on code from a lower level, only on classes from an upper level.<br/>
                        <strong>Dependency Inversion</strong> - When the flow of information goes against your architecture flow, that is when you are tempted to make a lower level class depend on a higher level class, use dependency injection mechanisms to avoid it. The result will be that the lower level code will call an abstraction and that will ensure layer separation in the correct direction.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Clean code</h5>
                        Clean Code is a software development philosophy that emphasizes writing code that is readable, maintainable, and efficient. The way you ensure you obtain code with these properties is by following the SOLID principles:<br/>
                        - Single Responsibility Principle (SRP)<br/>
                        - Open/Closed Principle (OCP)<br/>
                        - Liskov Substitution Principle (LSP)<br/>
                        - Interface Segregation Principle (ISP)<br/>
                        - Dependency Inversion Principle (DIP)<br/><br/>

                        <strong>SRP</strong> - Any unit of code should have a single responsability, a single job to do<br/>
                        <strong>OCP</strong> - A class should be open to extension and close to modification.<br/>
                        <strong>LSP</strong> - Objects of a subclass should be substitutable for objects of its superclass without affecting the correctness of the program<br/>
                        <strong>ISP</strong> - Clients should not be forced to depend on interfaces they do not use<br/>
                        <strong>DIP</strong> - High-level modules should not depend on low-level modules. Abstractions should not depend on details<br/>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Let's put it toghether</h5>
                        I want to have code that is scalable, maintainable and flexible. By using Clean Architecture I obtained exactly that. 
                        SOLID principles provides the mechanisms and building tools to achive this type of architecture. I see a kind of ladder: from lowest scale to the highest there is
                        SOLID principles, Design Patterns and Architecture. At each step you are increasing the subjects/scope from variables and lines of code to modules and components.
                    </div>

                    <h5>The implementation</h5>
                    <div className='paragraph'>
                        <h5 className='title'>Layers</h5>
                        Having the application structure respect a number of layers is crucial for having a clear separation of concerns and loose connections between components.
                        The number of layers is not fixed. Depends on the complexity of your project and the size of it. A side-effect I noticed showing up is that in a situation I needed to 
                        introduce a new class or a pattern of classes, it forced me to clearly decide on the intent of that class, its responsibility so that I can place it in the correct layer.
                        In general I believe this is a consequence of using well known and understood design structures: it lets you make much fewer unintentional mistakes.
                    </div>

                    <div className='paragraph'>
                        I decided to have 4 layers structure:<br/><br/>
                        1. <strong>Presentation Layer</strong>:<br/>
                        <span className='tab'/>a) This layer will be responsible for receiving the requests and returning collected data in the best way for the user of the API<br/>
                        <span className='tab'/>b) Will contain the API - Lambda Gateway<br/>
                        <span className='tab'/>c) Will contain the Delegate classes. The interface that connects to Application layer. Formats the data
                        retrieved from lower layers ensuring appropriate presentation of information to the user. They will call business logic by using the use-cases classes<br/><br/>
                        2. <strong>Application Layer</strong>:<br/>
                        <span className='tab'/>a) Will be the safest and most resistent to change layers. <br/>
                        <span className='tab'/>b) Will contain entities classes and use cases<br/><br/>
                        3. <strong>Persistance Layer</strong>:<br/>
                        <span className='tab'/>a) Retrieves data from the data store based on requests from the business layer<br/>
                        <span className='tab'/>b) Stores data to the data store as directed by the business layer<br/>
                        <span className='tab'/>c) Ensures data consistency and accuracy<br/>
                        <span className='tab'/>d) Protects data from unauthorized access or modification.<br/>
                        <span className='tab'/>e) Optimizes data access and storage operations for performance. I'm doing this by using local cache classes<br/>
                        <span className='tab'/>f) Hides the complexities of the underlying data storage mechanism from the rest of the application.<br/>
                        <span className='tab'/>g) Will contain DAO classes (will implement the responsabilities of the layer), DataSource interface classes, Mappers if needed<br/><br/>
                        4. <strong>Database Layer</strong>:<br/>
                        <span className='tab'/>a) Will contain the DBService class. This will run the DynamoDb queries<br/>
                        <span className='tab'/>b) Will contain the DataGateway classes. These will orchestrate DB queries to obtain data from multiple DB tables<br/>
                    </div>

                    <div className='paragraph'>
                        The image below (fig 1) gives a representation of how these layers communicate between them. Is shows the flow of information initiated by the user:
                    </div>
                    <img alt='architectural layers' className='img-row' src={layers} />

                    <div className='paragraph'>
                        Another way of visualising how layers intract one on top of the other, at the center being the mose sensitive and the most robust 
                        part of you code is shown in this image(fig 2):
                    </div>
                    <img alt='architectural layers' className='img-row' src={layers2} />

                    <div className='paragraph'>
                        Data passed between layers should be the most simple data objects, without functionality. For me it was a good mental exercise to decide what exact properties should cross the layers. I consider that ideally only the absolute minimum required information should pass the boundaries - information that comes directly from the user interaction with the app. To eventually complete the entire operation the user is asking you probably will need a lot more information than what crossed the presentation layer for example but you can use the use cases to obtain the rest which should be already present in the system and easily accessible.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Inversion of Control</h5>
                        After showing the layers I decided to implement and respect, I want to mention this software principle because it's the most important tool that allows up to keep the boundaries between those layers.
                        When we want to force the flow of code execution to go in the direction of the layers, we need to invert the control and to decouple the dependent classes. Inversion of control includes
                        <strong> Dependency Inversion</strong> for inverting function code execution and <strong>Dependency Injection</strong> for solving the same issue but when instantiating dependency classes.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Dependency Inversion</h5>
                        Let's say that my Lamda code receives a request to "Create a robot". The request will be received by the <strong>Presentation</strong> layer. From there I will
                        call the <inline-code>CreateRobot</inline-code> Use Case in the <strong>Application</strong> layer. This is the most sensitive of the layers and I want to keep it without dependencies on other classes 
                        part of the upper layers. If the Use Case class is calling <inline-code>CreateRobotDAO</inline-code> from <strong>Persistance</strong> layer, that will break this rule.
                    </div>
                    <img alt='dependency inversion' className='img-row high' src={dependency_inversion} />
                    <div className='paragraph'>
                        The way dependency inversion works is that you make the client class, in this case <inline-code>CreateRobot</inline-code> depends only on an interface. 
                        The implementation will be in the correct layer but the depency arrow is pointing in the correct direction. <inline-code>CreateRobot</inline-code> class will not depend on 
                        the <inline-code>CreateRobotDAO</inline-code>. <inline-code>CreateRobotDAO</inline-code> will implement the <inline-code>CreateRobotSource</inline-code> interface. The same 
                        situation applies with classes <inline-code>CreateRobotDAO</inline-code>, <inline-code>RobotsDataSource</inline-code> interface and <inline-code>RobotsDataGateway</inline-code>.
                        <br/>
                        Another way to see how layer separation works is to observe that classes in Application layer <strong>don't know</strong> about
                        classes in Persistence layer or Presentation layer. Classes the Persistence layer <strong>know</strong> about classes in Application layer
                        but <strong>know nothing</strong> about classes in the Database layer. A simple way to visualize this is when you look at the lines that represent layers limits,
                        all the arrows should point in the innermost layer - Application layer.
                    </div>


                    <div className='paragraph'>
                        <h5 className='title'>Dependency Injection</h5>
                        This is a technique in which an object or function receives the component classes that it requires, as opposed to creating them internally.
                        In my case, <strong> Dependency Injection</strong> comes in and removes any knowledge about which concrete class will implement the functionality that crosses the layer boundary. This knowledge will be segregated in 
                        a completely different part of the application in a designed class called typically <strong>injector</strong>. Normally this would be accomplished by an entire framework like <strong>Spring Boot</strong> for Java.
                        In my attempt to reduce the number of external frameworks I depend upon, I decided to give a try on doing this task manually.
                    </div>

                    <div className='paragraph'>
                        An important point about dependency injection is to choose where exactly you inject dependencies. Either you choose to use a framework or do it manually, like I will shortly show, you need to 
                        decide which classes will know about the injector. So for this reason, we normally accept a single point to be the dirtiest of all and only that will be the injection point. This is my injector class:

                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`package internal

import (
	application "example.com/on_path_robotics2/application"
	data "example.com/on_path_robotics2/data"
	framework "example.com/on_path_robotics2/framework"
)

func GetDBService() *framework.DBService {
	db := framework.GetDbService()
	return db
}

func GetRobotsDataGateway() framework.RobotsDataGateway {
	return framework.RobotsDataGateway{Db: GetDBService()}
}

func GetGetRobotsRemote() data.GetRobotsRemote {
	return data.GetRobotsRemote{RemoteDataSource: GetRobotsDataGateway()}
}

func GetGetRobots() *application.GetRobots {
	return &application.GetRobots{Source: GetGetRobotsRemote()}
}

func GetCreateRobot() *application.CreateRobot {
	return &application.CreateRobot{}
}

func GetLambdaGateway() framework.LambdaGateway {
	return framework.LambdaGateway{GetRobotsUseCase: GetGetRobots(), CreateRobotUseCase: GetCreateRobot()}
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className='paragraph'>
                        I build my "Injector" simply as a file inside the <inline-code>internal</inline-code> package.
                        As you can see, this file has dependencies on all the layers. I disregarded all the rules and principles here. 
                        The value I get for this barbaric attitude is stronger layer boundaries and also a much better structured code 
                        that becomes more resistant to bugs and issues. I want to repeat the point that I get stronger layer boundaries 
                        because only this class knows what concrete classes will be supplied to the components that request abstractions.
                    </div>

                    <div className='paragraph'>
                        I use this injector in only one place, in <inline-code>main.go</inline-code>
                        As mentioned in the book Clean Architecture by Robert C. Martin, <span className='quote'>"It is in this Main component that dependencies should be injected by a Dependency Injection framework. Once they are injected into Main, Main should distribute those dependencies normally, without using the framework."</span>

                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`package main

import (
	"errors"
	"regexp"

	"github.com/aws/aws-lambda-go/events"
	"github.com/aws/aws-lambda-go/lambda"

	internal "example.com/on_path_robotics2/internal"
)

func main() {
	lambda.Start(handler)
}
/
func handler(request events.APIGatewayProxyRequest) (events.APIGatewayProxyResponse, error) {
	matched, err := regexp.Match('.*/robots$', []byte(request.Path))
	if err == nil && matched {
		return internal.GetLambdaGateway().HandleRequest(request)
	}

	matched, err = regexp.Match('.*/get_robots$', []byte(request.Path))
	if err == nil && matched {
		return internal.GetLambdaGateway().HandleRequest(request)
	}

	matched, err = regexp.Match('.*/create_robot$', []byte(request.Path))
	if err == nil && matched {
		return internal.GetLambdaGateway().HandleCreateRobotRequest(request)
	}

	return internal.GetLambdaGateway().GetInvalidRequestResponse(request), errors.New("path not defined. Request path is " + request.Path)
}`}
                        </SyntaxHighlighter>
                    </div>
                    
                    <div className='paragraph'>
                        From now on, all components are asking for the dependencies in the constructor:
                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`package framework

import (
	"github.com/aws/aws-lambda-go/events"
)

type LambdaGateway struct {
	GetRobotsUseCase   *application.GetRobots
	CreateRobotUseCase *application.CreateRobot
}


func (l LambdaGateway) GetInvalidRequestResponse(request events.APIGatewayProxyRequest) events.APIGatewayProxyResponse {
	return events.APIGatewayProxyResponse{
		StatusCode: http.StatusBadRequest,
		Body:       '{"error": "Invalid request"}',
	}
}

func (l LambdaGateway) HandleRequest(request events.APIGatewayProxyRequest) (events.APIGatewayProxyResponse, error) {
	var requestBody GetRobotsRequest
	err := json.Unmarshal([]byte(request.Body), &requestBody)

	if err != nil {
		fmt.Printf("Error parsing request body: %v", err)
		errorResp, err := generateErrorReponse()
		addHeaders(&errorResp, *requestBody.Source)
		return errorResp, err
	}

	getRobotsDTO := dto.GetRobotsDTO{Id: *requestBody.FirstName, Name: *requestBody.LastName}
	robotData := l.GetRobotsUseCase.Invoke(getRobotsDTO)

	response, err := generateResponse(robotData)
	addHeaders(&response, *requestBody.Source)
	return response, err
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Entites</h5>
                        Entity classes are the most fundamental concepts in my project. The are very close to the business rules, in the most inner circle on the diagram above. 
                        Imagine that an entity classs could be usable by multiple applications across an enterprise, this is how low level they should be. 
                        I tried to keep my entity classes only as data structure, even though it is not necessary.
                    </div>

                    <div className='paragraph'>
                        This is an example of an entity class:
                        
                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`package application

type Robot struct {
	RobotId string
	Name    string
}
`}
                        </SyntaxHighlighter>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Use Cases</h5>
                        My documentation here is <strong>Clean Architecture by Elijan Lewis</strong>. <br/>

                        I make heavy use of this concept. All the logic will be built around UseCase classes. A “Use Case” will correspond to a very specific task, an application-specific business rule. They are units of functionality
                        that could be comparable to a physical action in a real situation. Use cases help coordinate the way data flows to and from entities. In the book he suggests that
                        there should be a complete "Use Case Layer". I build these classes inside my "Application Layer", because they should be as resistant to change as the underlying entities. This is 
                        a situation where I intentionally deviated from the theory in favor of practical efficiency.
                    </div>

                    <div className='paragraph'>
                        These are some examples of use case class:
                        
                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`// USE CASE
type GetRobots struct {
	Source GetRobotsSource
}

func (g *GetRobots) Invoke(userId *string) []Robot {
	return g.Source.GetRobots(userId)
}

type CreateRobot struct {
	Source CreateRobotSource
}

func (g CreateRobot) Invoke(data CreateRobotData) bool {
	g.Source.CreateRobot(data)
	return true
}
`}
                        </SyntaxHighlighter>
                        The <inline-code>GetRobots</inline-code> use case has only one method, called <inline-code>Invoke</inline-code> that requires the userId to be passed. <br/>
                    </div>

                    <div className='paragraph'>
                        I want that a Use Case class to be as minimal as possible: the task to be very clear and to require the least amount of information to get the job done. Use cases will
                        be combined in the upper layers to obtain more complex tasks and to get auxiliary data. 
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Inside a use case</h5>
                        I consider necessary for clarifying the concept of a use case to show my own implementation. I will take an example of a use case from another project so you will not find this in the source code provided here.
                    </div>

                    <img alt='example of use case with classes' className='img-row high' src={new_admision_use_case} />

                    <div className='paragraph'>
                        In the image above I show the name of the use case: <inline-code>NewAdmission</inline-code>, the layers and the classes implemented in each of these layers. We can start with the UI, in the <strong>Framework</strong> layer because this is where the user will want to add "a new admission". 
                        As per the layer separation described before, the next contact is with the <strong>Presentation</strong> layer and more specifically with the <inline-code>NewAdmissionPresentation</inline-code> class.
                    </div>

                    <div className='paragraph'>
                        👉 The class <inline-code>NewAdmissionPresentation</inline-code> gets injected only the necessary use cases to complete its responsibilities.
                        You can see here that the class <inline-code>NewAdmissionPresentation</inline-code> also requires the use case <inline-code>GetPatient</inline-code>. At this level I used clear, simple, precise use cases to provide the UI in the <strong>Framework</strong> layer all it requires to display screen elements<br/>
                        👉 <inline-code>NewAdmission</inline-code> use case has as source of data, the interface <inline-code>NewAdmissionSource</inline-code> <br/>
                        👉 This interface is implemented in the <strong>Data</strong> layer by the <inline-code>NewAdmissionRemote</inline-code> class. This is where I use all other necessary components in the <strong>Data</strong> layer to obtain good data management across my app. For example, here I save to a local temporary cache
                        some data(<inline-code>AdmissionCache</inline-code>), I map received data from the format received from DB to app entities(<inline-code>AdmissionMapper</inline-code>) and so on.<br/>
                        👉 Again, the source of data in <inline-code>NewAdmissionRemote</inline-code> will be an interface: <inline-code>AdmissionDataSourceRemote</inline-code> <br/>
                        👉 Implemented finally in the <strong>Framework</strong> layer by <inline-code>AdmissionRemoteDataGateway</inline-code>. This class, together with <inline-code>AdmissionDataSourceRemote</inline-code> holds all the necessary operations related to the corresponding entity, in this case <inline-code>Admission</inline-code>.
                        <inline-code>AdmissionRemoteDataGateway</inline-code> will be used by all the use cases related to the Admission entity.
                        <inline-code>FirestoreDbService</inline-code> holds the most basic CRUD operations related to all entities in the app so it will be used by most use cases.
                    </div>

                    <div className='paragraph'>
                        This is an example of how a use case is built. I did this throughout the app for each independent action and combined the use cases inside the Presentation layer to obtain the full functionality. This is the only way I implemented functionality.
                    </div>

                    <div className='side-story mt-4'>
                        <h2>Side story: GetString use case</h2>

                        <div className='paragraph'>
                            An interesting use case I decided to build was a correspondent to <inline-code>getString</inline-code> from Android framwork. The goal was to be able to have a mechanism to use texts throughout the app with regard to the user selected language.
                            The motivation to create my own mechanism was the fact that I prefer to have my own code rather than a third party library if the context permits. That is I don't want to reinvent the wheel but if I can spend a reasonable small amount of time writing
                            my own solution, I will potentially save myself from other troubles like keeping the package updated or even security risk by supply chain attacks.
                        </div>

                        <img alt='testing an use case' className='img-row md' src={get_string} />

                        <div className='paragraph'>
                            The issue of getting a text by the id number and to receive it in the correct language was a particular situation to construct a <strong>Use Case</strong>. In this situation there is no need for a remote data source.
                            The challenge was to:<br/>
                             - be able to easily add new texts as needed by the UI<br/>
                             - requests the text in the Presentation layer by using a name correlated to an int value<br/>
                             - request the text without knowing the language value in the user profile<br/>
                             - complete the requirements above without breaking the layer separation rules<br/>
                            The image above shows the components I made use of and next I am adding snippets of code to show the actual implementation. This example is from another project than the one I made use so far. Tha language is Dart since the project is using the Flutter framework.
                        </div>


                        <div className='paragraph'>
                            The <inline-code>AppStrings</inline-code> class only contains a map of the text id to an object containing the translated version of each language
                            
                            <SyntaxHighlighter  language='dart' style={dark} customStyle={codeStyle}>
                                {`
enum Language {
  romanian(value: 0),
  english(value: 1);

  const Language({required this.value});

  final int value;
}

class AppStrings {
  ...
  static const int button_add = 23;
  static const int invoice_reg_nr = 125;
  static const int invoice_address = 126
  ...

  // This is where all the string will be defined
  static final Map<int, Map<int, String>> texts = {
    button_add: {
      Language.romanian.value: "Adaugă",
      Language.english.value: "Add",
    },
    invoice_reg_nr: {
      Language.romanian.value: 'Nr ord ref comerțului',
      Language.english.value: 'Co register number',
    },
    invoice_address: {
      Language.romanian.value: 'Sediul',
      Language.english.value: 'Address',
    },
  }
}
`}
                            </SyntaxHighlighter>
                        </div>

                        <div className='paragraph'>
                            Inside <inline-code>Presentation</inline-code> class I inject the <inline-code>GetString</inline-code> use case class. I use a child of the Presentation class for each screen. Each of the Presentation implementation is injected into the screen Widget
                            
                            <SyntaxHighlighter  language='dart' style={dark} customStyle={codeStyle}>
                                {`
class Presentation {
  final GetString getStringImpl;

  Presentation(this.getStringImpl);

  String getString(int resourceId, [List<String>? arguments]) {
    if (arguments == null) {
      return getStringImpl.getString(resourceId);
    } else {
      return getStringImpl.getStringWithValues(resourceId, arguments);
    }
  }
}
`}
                            </SyntaxHighlighter>
                        </div>

                        <div className='paragraph'>
                            The <inline-code>GetString</inline-code> class looks like this:
                            
                            <SyntaxHighlighter  language='dart' style={dark} customStyle={codeStyle}>
                                {`class GetString {
  final TextsSource textsSource;

  const GetString(this.textsSource);

  String getString(int resourceId) {
    return textsSource.getString(AppStrings.texts[resourceId]!);
  }

  String getStringWithValues(int resourceId, List<String> arguments) {
    return textsSource.getStringWithValues(AppStrings.texts[resourceId]!, arguments);
  }
}
`}
                            </SyntaxHighlighter>
                        </div>

                        <div className='paragraph'>
                            And the most important <inline-code>GetTextLocal</inline-code> looks like this:
                            
                            <SyntaxHighlighter  language='dart' style={dark} customStyle={codeStyle}>
                                {`class GetTextsLocal implements TextsSource {

  final OnDevicePreferencesDataSource preferencesSource;
  final GetUserProfileLocal userLocalSource;
  final GetUserProfileCombined userCombinedSource;
  final StoreLangOnDeviceLocal storeLang;

  const GetTextsLocal(this.preferencesSource, this.userLocalSource, this.userCombinedSource, this.storeLang);

  @override
  String getString(Map<int, String> texts) {
    int? language = _getLang();
    if (language == null) {
      _storeFromRemote();
      language = 0;
    }

    if (texts[language] != null) {
      return texts[language]!;
    } else {
      return "";
    }
  }

  @override
  String getStringWithValues(Map<int, String> texts, List<String> arguments) {
    int? language = _getLang();
    if (language == null) {
      _storeFromRemote();
      language = 0;
    }

    if (texts[language] != null) {
      String t = texts[language]!;
      for (int i in List<int>.generate(arguments.length, (i) => i + 0)) {
        t = t.replaceFirst("{$i}", arguments[i]);
      }
      return t;
    } else {
      return "";
    }
  }

  int? _getLang() {
    if (userLocalSource.getUserProfile() != null) {
      return userLocalSource.getUserProfile()!.lang;
    } else {
      return preferencesSource.getLanguage();
    }
  }

  void _storeFromRemote() {
    userCombinedSource.getUserProfile().then((res) {
      if(res.ok) {
        storeLang.setLanguage(res.data!.lang);
      }
    });
  }
}                                
`}
                            </SyntaxHighlighter>
                        </div>


                    </div>


                    <div className='side-story mt-4'>
                        <h2>Side story: Testing Use Cases</h2>

                        <div className='paragraph'>
                            I noticed that a posibility becomes visible when you start building an app on top of a set of Use Cases: testing a Use Case. This is surely an integration test. The value I see is that because your app is a small set of use cases working toghether, if you complete good tests for each use case you most probably have covered a large part of the functionality. You also can mock all dependencies the same way you add them in the injector. This can generate a lot of code but also gives you a lot of control.
                        </div>

                        <img alt='testing an use case' className='img-row high' src={testusecase} />
                    </div>
                    
                    <div className='paragraph'>
                        <h5 className='title'>File structure</h5>
                        I also want to mention how I organized the files and folders to minimize the time I need for searching. Each layer will be represented by a folder in the directory structure. 
                        Using go as the programming language for the server, this will mean that each layer will be a package. To check that layer dependencies are satisfied, we can 
                        can simply check the import statements on each file in the package. Layer dependencies to be satisfied means that each layer should have dependencies only on inner layers,
                        never one layer should depend on an outer layer. See fig 2.
                    </div>

                    <div className='paragraph'>
                        I also decided that multiple similar classes can be placed in the same file such for entities and use cases. 
                    </div>

                    
                    <div className='paragraph'>
                        <h5 className='title'>The whole picture</h5>
                        Trying to make a relevant entire picture of the entire architectural implementation I will present the implementation of a single use case. 
                    </div>


                    <div className='paragraph'>
                        Here is the entire code base I wrote for this project for the React front end and for the Lambda function:<br/>
                        <a target="_blank" rel='noreferrer' href='https://github.com/lupascugabrielcristian/golang-webapp-front'>React front-end</a><br/>
                        <a target="_blank" rel='noreferrer' href='https://github.com/lupascugabrielcristian/golang-webapp-lambda'>Lambda function</a><br/>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>References</h5>
                        Clean Architecture by Robert C. Martin<br/>
                        Clean Mobile Architecture - Become an Android, iOS, Flutter Architect by Petros Efthymiou<br/>
                        Clean Architecture - Advanced and Effective Strategies Using Clean Architecture Principles by Elijan Lewis<br/>
                    </div>


                    <h5>Final thoughts</h5>
                    <div className='paragraph'>
                        I consider my template project complete so I will stop here this series of deep dives posts. I intent to add more details and corrections in the future as I personally improve my knowledge. I hope you
                        found something usefull and if you can provide any feedback feel free to reach out by any means. All  sugestions will be much appreciated. The end ... for now.
                    </div>

                    <div className='articles mt-5'>
                        <Articol title='GoLang Web app on AWS (Part 2) - Front-end and CORS' to={'/dp-golang-webapp-cors'} />
                        <Articol title='GoLang Web app on AWS (Part 3) - DynamoDB' to={'/dp-golang-webapp-dynamodb'} />
                    </div>

                    <div className='mb-10'></div>
                </div>
            </div>
        )
    }
}

export default GoLangWebAppArchitecture;